import React from 'react';
import { X, Zap, Check, ArrowRight } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const TIER_FEATURES = {
  free: {
    name: 'Free',
    price: '$0',
    period: 'forever',
    color: 'slate'
  },
  pro: {
    name: 'Pro',
    price: '$9.99',
    period: '/month',
    color: 'blue',
    popular: true
  },
  enterprise: {
    name: 'Enterprise',
    price: 'Custom',
    period: 'pricing',
    color: 'purple'
  }
};

const UpgradeLimitModal = ({ isOpen, onClose, limits, limitError }) => {
  const brand = useBrand();

  if (!isOpen) return null;

  const currentTier = limits?.tier || limitError?.tier || 'free';
  const usage = limitError?.usage || limits?.products?.usage || { daily: 0, monthly: 0 };
  const tierLimits = limitError?.limits || limits?.products?.limits || {};

  const handleUpgrade = (tier) => {
    // Navigate to pricing page
    const upgradeUrl = brand?.features?.limits?.upgradeUrl || '/pricing';
    window.location.href = `${upgradeUrl}?tier=${tier}`;
  };

  const handleContact = () => {
    const contactUrl = brand?.features?.limits?.contactUrl || '/contact';
    window.location.href = contactUrl;
  };

  // Get tier limits from brand config or use defaults
  const getTierLimits = (tier) => {
    const brandTierDefaults = brand?.features?.limits?.tierDefaults?.[tier];
    if (brandTierDefaults?.products) {
      return brandTierDefaults.products;
    }
    // Fallback defaults
    const defaults = {
      free: { sessionMax: 3, dailyMax: 10, monthlyMax: 50 },
      pro: { sessionMax: 10, dailyMax: 50, monthlyMax: 500 },
      enterprise: { sessionMax: 50, dailyMax: 500, monthlyMax: 10000 }
    };
    return defaults[tier] || defaults.free;
  };

  const tiers = ['free', 'pro', 'enterprise'];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Upgrade Your Plan</h2>
            </div>

            {/* Current usage info */}
            {limitError && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  {limitError.message || `You've reached your ${currentTier} tier limit.`}
                </p>
                <div className="mt-2 flex gap-4 text-xs text-amber-600">
                  <span>Daily: {usage.daily}/{tierLimits.dailyMax || '?'}</span>
                  <span>Monthly: {usage.monthly}/{tierLimits.monthlyMax || '?'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Tier comparison */}
          <div className="p-6">
            <div className="grid md:grid-cols-3 gap-4">
              {tiers.map((tier) => {
                const tierInfo = TIER_FEATURES[tier];
                const tierProductLimits = getTierLimits(tier);
                const isCurrentTier = tier === currentTier;
                const isUpgrade = tiers.indexOf(tier) > tiers.indexOf(currentTier);

                return (
                  <div
                    key={tier}
                    className={`relative rounded-xl border-2 p-5 transition-all ${
                      tierInfo.popular
                        ? 'border-blue-500 shadow-lg shadow-blue-100'
                        : isCurrentTier
                          ? 'border-slate-300 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Popular badge */}
                    {tierInfo.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                        Most Popular
                      </div>
                    )}

                    {/* Current tier badge */}
                    {isCurrentTier && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-500 text-white text-xs font-medium rounded-full">
                        Current Plan
                      </div>
                    )}

                    <div className="text-center mb-4 pt-2">
                      <h3 className="text-lg font-bold text-slate-900">{tierInfo.name}</h3>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-slate-900">{tierInfo.price}</span>
                        <span className="text-slate-500 text-sm">{tierInfo.period}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        <span>{tierProductLimits.sessionMax} products per session</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        <span>{tierProductLimits.dailyMax} products per day</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        <span>{tierProductLimits.monthlyMax.toLocaleString()} products per month</span>
                      </li>
                    </ul>

                    {/* Action button */}
                    {isCurrentTier ? (
                      <button
                        disabled
                        className="w-full py-2.5 px-4 rounded-lg bg-slate-100 text-slate-400 font-medium cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : tier === 'enterprise' ? (
                      <button
                        onClick={handleContact}
                        className="w-full py-2.5 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        Contact Sales
                        <ArrowRight size={16} />
                      </button>
                    ) : isUpgrade ? (
                      <button
                        onClick={() => handleUpgrade(tier)}
                        className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                          tierInfo.popular
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-slate-900 hover:bg-slate-800 text-white'
                        }`}
                      >
                        Upgrade
                        <ArrowRight size={16} />
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full py-2.5 px-4 rounded-lg bg-slate-100 text-slate-400 font-medium cursor-not-allowed"
                      >
                        Downgrade
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer note */}
            <p className="mt-6 text-center text-sm text-slate-500">
              Upgrade anytime. Cancel anytime. No long-term commitments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeLimitModal;
